/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loading } from 'uikit/loaders';

import ListEmpty from './listEmpty';

import styles from '../styles/infiniteScrollList.module.scss';

const InfiniteScrollList = ({
  components = {},
  emptyStateBody = '',
  emptyStateBodyVariant = 'b01',
  emptyStateButtonColor = 'brand',
  emptyStateButtonDisabled = false,
  emptyStateButtonText = '',
  emptyStateButtonType = '',
  emptyStateButtonVariant = 'default',
  emptyStateHasAddIcon = true,
  emptyStateTitle = '',
  emptyStateTitleVariant = 'd02',
  groupBy = () => {},
  handleEmptyStateClick = () => {},
  hasButton = true,
  isGrouped = false,
  queryEnabled = true,
  queryFn,
  queryKey,
  renderGroupHeader = () => {},
}) => {
  const infiniteScrollQueryKey = Array.isArray(queryKey) ? queryKey : [queryKey];

  const { data, fetchNextPage, isLoading } = useInfiniteQuery({
    queryKey: infiniteScrollQueryKey,
    queryFn,
    enabled: queryEnabled,
    getNextPageParam: (lastPage) => {
      if (lastPage.data.pagination.is_last_page) return;
      return lastPage.data.pagination.page + 1;
    },
  });

  const items = data?.pages?.flatMap((page) => page.data.data);

  if (isLoading) return <Loading className={styles.loader} />;

  if (!items || !items.length) {
    return (
      <ListEmpty
        className={styles.emptyState}
        title={emptyStateTitle}
        titleVariant={emptyStateTitleVariant}
        body={emptyStateBody}
        bodyVariant={emptyStateBodyVariant}
        onButtonClick={handleEmptyStateClick}
        buttonColor={emptyStateButtonColor}
        buttonText={emptyStateButtonText}
        buttonType={emptyStateButtonType}
        buttonVariant={emptyStateButtonVariant}
        hasAddIcon={emptyStateHasAddIcon}
        hasButton={hasButton}
        isButtonTypeAi={emptyStateButtonType === 'ai'}
        isButtonDisabled={emptyStateButtonDisabled}
      />
    );
  }

  const { ListItem } = components;
  const ListItemElement = ListItem || null;

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={() => fetchNextPage()}
      hasMore={!data?.pages?.at(-1)?.data?.pagination?.is_last_page}
      loader={<Loading className={styles.loader} />}
    >
      {isGrouped
        ? Object.entries(groupBy(items)).map(([groupKey, groupItems]) => (
            <div key={groupKey} className={styles.group}>
              {renderGroupHeader(groupKey)}
              {groupItems.map((item) => (
                <ListItemElement key={`listItem-${item.key || item.id}`} {...item} />
              ))}
            </div>
          ))
        : items.map((listItem) => (
            <ListItemElement key={`listItem-${listItem.key || listItem.id}`} {...listItem} />
          ))}
    </InfiniteScroll>
  );
};

InfiniteScrollList.propTypes = {
  components: PropTypes.shape({
    ListItem: PropTypes.elementType,
  }),
  emptyStateBody: PropTypes.string,
  emptyStateBodyVariant: PropTypes.string,
  emptyStateButtonColor: PropTypes.string,
  emptyStateButtonText: PropTypes.string,
  emptyStateButtonType: PropTypes.string,
  emptyStateButtonVariant: PropTypes.string,
  emptyStateButtonDisabled: PropTypes.bool,
  emptyStateHasAddIcon: PropTypes.bool,
  emptyStateTitle: PropTypes.string,
  emptyStateTitleVariant: PropTypes.string,
  groupBy: PropTypes.func,
  handleEmptyStateClick: PropTypes.func,
  hasButton: PropTypes.bool,
  isGrouped: PropTypes.bool,
  queryEnabled: PropTypes.bool,
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  ]).isRequired,
  renderGroupHeader: PropTypes.func,
};

export default React.memo(InfiniteScrollList);
